import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config/api';
import { Modal, Button, Form, Alert, Badge, Spinner } from 'react-bootstrap';

/**
 * InvestorVehicleModal — admin popup to assign vehicles to an investor.
 * Lists ALL vehicles regardless of store (investors are not store-associated);
 * saving replaces the investor's full assignment set.
 */
export function InvestorVehicleModal({ investor, show, onHide, apiCall, onSaved }) {
  const [vehicles, setVehicles] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !investor) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [vehiclesRes, assignedRes] = await Promise.all([
          apiCall(`${API_BASE_URL}/vehicles?limit=1000`),
          apiCall(`${API_BASE_URL}/investors/${investor.id}/vehicles`),
        ]);
        if (cancelled) return;
        setVehicles(vehiclesRes.data || []);
        setSelectedIds(new Set((assignedRes.data?.vehicles || []).map(v => v.id)));
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load vehicles');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    return () => { cancelled = true; };
  }, [show, investor, apiCall]);

  const filteredVehicles = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return vehicles;
    return vehicles.filter(v =>
      `${v.year} ${v.make} ${v.model} ${v.license_plate} ${v.vin} ${v.store_name || ''}`
        .toLowerCase()
        .includes(term)
    );
  }, [vehicles, search]);

  const toggleVehicle = (vehicleId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(vehicleId)) {
        next.delete(vehicleId);
      } else {
        next.add(vehicleId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiCall(`${API_BASE_URL}/investors/${investor.id}/vehicles`, {
        method: 'PUT',
        body: JSON.stringify({ vehicle_ids: Array.from(selectedIds) }),
      });
      if (onSaved) onSaved();
      onHide();
    } catch (err) {
      setError(err.message || 'Failed to save vehicle assignments');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Assign Vehicles — {investor?.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Control
          type="text"
          placeholder="Search by year, make, model, plate, VIN, or store..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-3"
        />

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="text-center py-4 text-muted">No vehicles found.</div>
        ) : (
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            {filteredVehicles.map(vehicle => (
              <div
                key={vehicle.id}
                className="d-flex align-items-center justify-content-between border-bottom py-2"
              >
                <Form.Check
                  type="checkbox"
                  id={`investor-vehicle-${vehicle.id}`}
                  checked={selectedIds.has(vehicle.id)}
                  onChange={() => toggleVehicle(vehicle.id)}
                  label={
                    <span>
                      {vehicle.year} {vehicle.make} {vehicle.model}
                      {' '}
                      <span className="text-muted">
                        · {vehicle.license_plate}{vehicle.license_plate_state ? ` (${vehicle.license_plate_state})` : ''}
                      </span>
                    </span>
                  }
                />
                <Badge bg="light" text="dark">{vehicle.store_name || 'No store'}</Badge>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <span className="text-muted">
          {selectedIds.size} vehicle{selectedIds.size === 1 ? '' : 's'} selected
        </span>
        <div>
          <Button variant="secondary" onClick={onHide} disabled={saving} className="me-2">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Saving...' : 'Save Assignments'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
